import BigNumber from 'bignumber.js';

/**
 * Function to seperate thousands with commas in a number | string and return a string
 * return undefined if input is not a number
 * @param value
 * @returns
 */
export const thousandSeparator = (options: { value: string | number; separator?: string }) => {
  const { value, separator = ',' } = options;

  const invalidInput = !validateInput(value);
  if (invalidInput) return;

  const [integerPart, decimalPart] = `${value}`.split('.');

  const convertedIntegerPart = integerThousandSeparator(integerPart, separator);

  return decimalPart ? `${convertedIntegerPart}.${decimalPart}` : convertedIntegerPart;
};

/**
 * Functions to format a number to a string with a specific number of decimals and a specific separator between thousands | commas
 * return f if input is not a number
 *
 * Default decimals = 8
 * Default separator = ,
 *
 * @param options
 * @returns
 */
export const formatAmountByDecimals = (options: {
  value: string | number;
  decimals?: number;
  separator?: string;
  keepZero?: boolean;
  trailingZeros?: boolean;
  prefix?: string;
  suffix?: string;
}) => {
  const { value, decimals = 8, separator = ',', keepZero = false, trailingZeros = true } = options;

  const isNegativie = new BigNumber(value).isNegative();

  const invalidinput = !validateInput(value);
  if (invalidinput) return '';

  const initialValue = typeof value === 'number' ? new BigNumber(value).toString() : value;
  const absValue = isNegativie ? initialValue.replace('-', '') : initialValue;
  const hasDot = `${absValue}`.includes('.');
  const [integerPart, decimalPart] = `${absValue}`.split('.');

  const convertIntegerPart = keepZero ? integerPart : new BigNumber(integerPart).toString();
  const formattedIntegerPart = integerThousandSeparator(convertIntegerPart, separator);

  const convertDecimalPart = decimalPart;
  let parsedDecimalPart = decimalPart && convertDecimalPart.replace('.', '');

  let result = '';
  if (!parsedDecimalPart && !trailingZeros) {
    result = `${formattedIntegerPart}${hasDot ? '.' : ''}`;
  } else {
    // remove any dots from decimalPart
    // let formattedDecimalPart = parsedDecimalPart ? `${parsedDecimalPart.slice(0, decimals)}` : `${'0'.repeat(decimals)}`;
    let formattedDecimalPart = parsedDecimalPart ? `${parsedDecimalPart.slice(0, decimals)}` : undefined;
    result = formattedDecimalPart ? `${formattedIntegerPart}.${formattedDecimalPart}` : `${formattedIntegerPart}`;
  }

  if (isNegativie) {
    result = `-${result}`;
  }

  if (options.prefix) {
    result = `${options.prefix} ${result}`;
  }

  if (options.suffix) {
    result = `${result} ${options.suffix}`;
  }

  return result;
};

const integerThousandSeparator = (value: string, separator: string) => {
  return value
    .split('')
    .reverse()
    .reduce((acc, curr, index) => {
      if (index % 3 === 0 && index !== 0) {
        return `${curr}${separator}${acc}`;
      }
      return `${curr}${acc}`;
    }, '');
};

/**
 * Function to validate inputs
 * @param value
 */
const validateInput = (value: string | number) => {
  return !new BigNumber(value).isNaN();
};

/**
 * Test Functions for Amount Input Component
 * Install Quokka extension in VS Code to run tests automatically
 */

const handleAmountInput = (input: string, decimals?: number): string => {
  const inputBig = new BigNumber(input);

  // Check if input is a valid number
  const invalidNumber = inputBig.isNaN();
  if (invalidNumber) {
    throw new Error('INVALID_NUMBER');
  }

  // Format input
  const formattedInput = formatAmountByDecimals({
    value: input,
    decimals
  });

  if (!formattedInput) {
    throw new Error('INVALID_NUMBER');
  }

  return formattedInput;
};

// Test Cases
const TEST_CASES = {
  normal: {
    integer: '12345678',
    withDecimals: '12345678.87654321',
    withLeadingZero: '01234567.7654321',
    withTrailingZero: '012345678.87600000'
  },
  negative: {
    integer: '-12345678',
    withDecimals: '-12345678.87654321',
    withLeadingZero: '-01234567.7654321',
    withTrailingZero: '-012345678.87600000'
  },
  invalid: {
    nonDigitalString: 'asd1',
    startingDot: '.1223.32192',
    middleDots: '0.....0'
  }
};

const test = (input: string, decimals?: number) => {
  try {
    return handleAmountInput(input, decimals);
  } catch (error) {
    return error;
  }
};

const normalInput_integer = test(TEST_CASES.normal.integer);
normalInput_integer;

const normalInput_withDecimals = test(TEST_CASES.normal.withDecimals);
normalInput_withDecimals;

const normalInput_withLeadingZero = test(TEST_CASES.normal.withLeadingZero);
normalInput_withLeadingZero;

const normalInput_withTrailingZero = test(TEST_CASES.normal.withTrailingZero);
normalInput_withTrailingZero;

const invalid_nonDigitalString = test(TEST_CASES.invalid.nonDigitalString);
invalid_nonDigitalString;

const invalid_startingDot = test(TEST_CASES.invalid.startingDot);
invalid_startingDot;

const invalid_middleDots = test(TEST_CASES.invalid.middleDots);
invalid_middleDots;

const normalInput_negativeInteger = test(TEST_CASES.negative.integer);
normalInput_negativeInteger;

const normalInput_negativeWithDecimals = test(TEST_CASES.negative.withDecimals);
normalInput_negativeWithDecimals;

const normalInput_negativeWithLeadingZero = test(TEST_CASES.negative.withLeadingZero);
normalInput_negativeWithLeadingZero;

const normalInput_negativeWithTrailingZero = test(TEST_CASES.negative.withTrailingZero);
normalInput_negativeWithTrailingZero;
